// http.ts

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { getIdToken } from "../firebase/auth";
import { getApiPath } from "../../config/env";
import { ReactToastify } from "utils/error";

enum StatusCode {
  Unauthorized = 401,
  NotFound = 404,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
  BadRequest = 400,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  //'Access-Control-Allow-Origin': 'http://localhost:3004',
  //'Access-Control-Allow-Credentials': true,
  "X-Requested-With": "XMLHttpRequest",
};

// We can use the following function to inject the JWT token through an interceptor
// We get the `accessToken` from the localStorage that we set when we authenticate
// const injectToken = (config: AxiosRequestConfig): AxiosRequestConfig => {
const injectToken = async (config: AxiosRequestConfig): Promise<any> => {
  const REFRESH_TOKEN = false;
  //const rtnConfig = config;

  try {
    //const token = localStorage.getItem("accessToken");
    const token = await getIdToken(REFRESH_TOKEN);

    if (token != null && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error: any) {
    throw new Error(error);
  }
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: getApiPath(),
      headers,
      withCredentials: true,
    });

    http.interceptors.request.use(injectToken, (error) =>
      Promise.reject(error)
    );

    http.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        return this.handleError(response);
      }
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig
  ): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.patch<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  // Handle global app errors
  // We can handle generic app errors depending on the status code
  private handleError(error: any) {
    const { status } = error;
    const { AddToast } = ReactToastify();
    switch (status) {
      case StatusCode.InternalServerError: {
        // Handle InternalServerError
        AddToast(error?.data?.message, "error");
        break;
      }
      case StatusCode.Forbidden: {
        // Handle Forbidden
        AddToast(error?.data?.message, "error");
        break;
      }
      case StatusCode.Unauthorized: {
        // Handle Unauthorized
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        break;
      }
      case StatusCode.NotFound: {
        AddToast(error?.data, "error");
        break;
      }
      case StatusCode.BadRequest: {
        // Handle Forbidden
        AddToast(error?.data?.message, "error");
        break;
      }
    }

    return Promise.reject(error);
  }
}

const http = new Http();
export default http;
